import PropTypes from 'prop-types';
import { BsArrowCounterclockwise } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import { useError } from '#globalContexts/ErrorContext';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';

import { useRetryFailedSends } from '../../models/emailSends/mutations';

/**
 * @param {{isActive: boolean, canSend: boolean, retryableCount: number}} state
 * @returns {string} why the button is disabled, else how many emails it reschedules
 */
const buildRetryTitle = ({ isActive, canSend, retryableCount }) => {
	if (!canSend) return 'Versand ist in Ihrem aktuellen Abo nicht verfügbar';
	if (!isActive) return 'Nur bei aktiven Kampagnen möglich';
	return `${retryableCount.toLocaleString('de-DE')} fehlgeschlagene/übersprungene E-Mail(s) erneut einplanen`;
};

const RetryFailedButton = ({ campaignId, retryableCount, campaignStatus }) => {
	const { showSuccess } = useError();
	const retryFailed = useRetryFailedSends();
	const { hasCapability: canSend } = useCapabilityCheck('email.send');

	if (retryableCount === 0) return null;

	// Retrying is only allowed for active campaigns: an email account is only guaranteed to be
	// linked once the campaign has been activated, and the scheduler only sends for active campaigns.
	const isActive = campaignStatus === 'active';
	const isDisabled = !isActive || !canSend || retryFailed.isPending;

	const handleRetry = async () => {
		const { retried } = await retryFailed.mutateAsync(campaignId);
		showSuccess({
			title: 'Erneuter Versand geplant',
			message: `${retried} fehlgeschlagene/übersprungene E-Mail(s) wurden erneut eingeplant.`,
			autoClose: true,
		});
	};

	return (
		<Button
			onClick={handleRetry}
			variant="outline"
			size="sm"
			disabled={isDisabled}
			title={buildRetryTitle({ isActive, canSend, retryableCount })}
		>
			<BsArrowCounterclockwise className={`h-4 w-4 mr-2 ${retryFailed.isPending ? 'animate-spin' : ''}`} />
			{retryFailed.isPending ? 'Wird gesendet...' : 'Erneut senden'}
		</Button>
	);
};

RetryFailedButton.propTypes = {
	campaignId: PropTypes.string.isRequired,
	retryableCount: PropTypes.number.isRequired,
	campaignStatus: PropTypes.string,
};

export default RetryFailedButton;
